'use client';

import { useMemo } from 'react';
import Link from 'next/link';
import { Box, Typography } from '@mui/material';

import { TruncatedTooltip } from '@/components';
import { useT } from '@/hooks';
import type { EventResponse } from '@/types';
import { formatVolume, isEsportsEvent, isEventLive, isMarketResolved } from '@/utils';
import { getYesPrice } from './getYesPrice';

import { ROUTES } from '@/constants';

const VISIBLE_ROWS = 4;

// Each list's scroll container is capped to exactly VISIBLE_ROWS worth of
// height (row height + gap, both mirrored from the row Box sx below) — with
// more items than that, the list scrolls instead of growing the rail.
const LIVE_ROW_HEIGHT = 44;
const LIVE_ROW_GAP = 12; // px equivalent of the row Box's gap: 1.5
const LIVE_LIST_MAX_HEIGHT = VISIBLE_ROWS * LIVE_ROW_HEIGHT + (VISIBLE_ROWS - 1) * LIVE_ROW_GAP;

const HOT_ROW_HEIGHT = 28;
const HOT_ROW_GAP = 10; // px equivalent of the row Box's gap: 1.25
const HOT_LIST_MAX_HEIGHT = VISIBLE_ROWS * HOT_ROW_HEIGHT + (VISIBLE_ROWS - 1) * HOT_ROW_GAP;

// index.css force-hides scrollbars app-wide (`::-webkit-scrollbar { display: none
// !important }`, `scrollbar-width: none !important`) — these two lists are meant to
// be visibly scrollable, so beating that reset needs matching `!important`s here too.
// scrollbarColor is the standard property Firefox reads for thumb/track color;
// on modern Chromium/Edge, setting `scrollbar-width` also switches them onto this
// standard path (over the legacy ::-webkit-scrollbar-thumb below), so it must be
// set explicitly here too — otherwise those browsers fall back to their native
// (light/white) scrollbar color instead of the themed one.
const VISIBLE_SCROLLBAR_SX = {
  scrollbarWidth: 'thin !important',
  scrollbarColor: 'var(--border-color) transparent !important',
  '&::-webkit-scrollbar': {
    display: 'block !important',
    width: '6px !important',
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent !important',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: 'var(--border-color) !important',
    borderRadius: '999px',
  },
} as const;

// Whether an event surfaces in the "Live now" list — broader than the
// isEventLive() routing check below (also treats a live flag on any market
// as live, not just the event-level flag).
const hasLiveIndicator = (ev: EventResponse) =>
  !!ev.isLive || (ev.markets || []).some((m) => !!m?.isLive || !!(m as any)?.live);

const isEventOpen = (ev: EventResponse) =>
  (ev.markets || []).some((m) => m?.closed !== true && m?.active !== false) &&
  !isMarketResolved(ev.markets?.[0] as any);

// The events-list API never populates the event-level `volume24hr` — only the
// underlying markets carry a real one (mirrors how `volume` is the sum of each
// market's `volumeNum`). Summing those gives the event's actual today's volume.
const dailyVolume = (ev: EventResponse) =>
  (ev.markets || []).reduce((sum, m) => sum + (Number(m.volume24hr) || 0), 0);

// "Hot today" ranks strictly by dailyVolume; this is the same figure but falls
// back to all-time volume for display elsewhere (e.g. "Live now") when an event
// genuinely has no daily volume data at all.
const eventVolume = (ev: EventResponse) => dailyVolume(ev) || Number(ev.volume) || 0;

// Mirrors MarketCard's link logic: sports games go to their game-detail page,
// live (non-game) markets go to the live-market page, everything else to the
// regular market-details page — otherwise sports events land on the generic
// market page and render with the wrong layout.
const eventHref = (ev: EventResponse) => {
  if (ev.gameId) {
    return ROUTES.GAME_DETAIL(ev.homeTeam?.league || 'live', ev.id.toString());
  }
  const fallbackId = ev.slug || ev.markets?.[0]?.id || '';
  return isEventLive(ev)
    ? ROUTES.LIVE_MARKET_DETAILS(fallbackId)
    : ROUTES.MARKET_DETAILS(fallbackId);
};

interface FeaturedSideRailProps {
  /** Full trending event pool to draw "Live now" / "Hot today" from. */
  events: EventResponse[];
  /** The event already shown as the main featured slide — excluded from both lists. */
  excludeEventId?: string;
}

/**
 * Sits to the right of the featured event's graph, showing what's live right
 * now and the highest-volume markets today — a quick-scan companion to the
 * single event highlighted in the hero slide.
 */
export const FeaturedSideRail = ({ events, excludeEventId }: FeaturedSideRailProps) => {
  const { t } = useT();

  const pool = useMemo(
    () => events.filter((ev) => ev.id !== excludeEventId && isEventOpen(ev)),
    [events, excludeEventId],
  );

  const liveNow = useMemo(
    () => pool.filter((ev) => hasLiveIndicator(ev) && !isEsportsEvent(ev)),
    [pool],
  );

  const hotToday = useMemo(() => {
    const ranked = pool
      .filter((ev) => dailyVolume(ev) > 0)
      .sort((a, b) => dailyVolume(b) - dailyVolume(a));
    const max = Math.max(1, ...ranked.map(dailyVolume));
    return ranked.map((ev) => ({
      event: ev,
      heat: Math.max(6, Math.round((dailyVolume(ev) / max) * 100)),
    }));
  }, [pool]);

  return (
    <Box sx={{ display: { xs: 'none', sm: 'flex' }, flexDirection: 'column', gap: 2, minWidth: 0 }}>
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 3,
          p: 2,
          bgcolor: 'background.paper',
        }}
      >
        <Box
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: '0.875rem' }}>Live now</Typography>
          {!!liveNow.length && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'error.main' }} />
              <Typography
                variant="caption"
                sx={{ fontWeight: 700, color: 'error.main', fontFamily: 'monospace' }}
              >
                {liveNow.length}
              </Typography>
            </Box>
          )}
        </Box>

        {liveNow.length ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              maxHeight: LIVE_LIST_MAX_HEIGHT,
              overflowY: 'auto',
              pr: 0.5,
              ...VISIBLE_SCROLLBAR_SX,
            }}
          >
            {liveNow.map((ev, i) => {
              const prob = getYesPrice(ev.markets?.[0]);
              return (
                <Box
                  key={ev.id}
                  component={Link}
                  href={eventHref(ev)}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1.25,
                    minHeight: LIVE_ROW_HEIGHT,
                    flexShrink: 0,
                    textDecoration: 'none',
                    '&:hover .rail-title': { color: 'primary.main' },
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.secondary', width: 12, pt: '2px', fontFamily: 'monospace' }}
                  >
                    {i + 1}
                  </Typography>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <TruncatedTooltip title={ev.title ?? ''}>
                      <Typography
                        className="rail-title"
                        variant="body2"
                        sx={{
                          color: 'text.primary',
                          lineHeight: 1.35,
                          transition: 'color 0.15s ease',
                        }}
                        noWrap
                      >
                        {ev.title}
                      </Typography>
                    </TruncatedTooltip>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {formatVolume(eventVolume(ev))} vol.
                    </Typography>
                  </Box>
                  <Typography sx={{ fontWeight: 700, fontFamily: 'monospace', fontSize: '0.9rem' }}>
                    {Math.round(prob * 100)}%
                  </Typography>
                </Box>
              );
            })}
          </Box>
        ) : (
          <Typography
            variant="body2"
            sx={{ color: 'text.secondary', textAlign: 'center', py: 1.5 }}
          >
            {t('common.noData')}
          </Typography>
        )}
      </Box>

      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 3,
          p: 2,
          bgcolor: 'background.paper',
        }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: '0.875rem', mb: 1.5 }}>Hot today</Typography>

        {hotToday.length ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1.25,
              maxHeight: HOT_LIST_MAX_HEIGHT,
              overflowY: 'auto',
              pr: 0.5,
              ...VISIBLE_SCROLLBAR_SX,
            }}
          >
            {hotToday.map(({ event: ev, heat }) => (
              <Box
                key={ev.id}
                component={Link}
                href={eventHref(ev)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  minHeight: HOT_ROW_HEIGHT,
                  flexShrink: 0,
                  textDecoration: 'none',
                  '&:hover .rail-title': { color: 'primary.main' },
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <TruncatedTooltip title={ev.title ?? ''}>
                    <Typography
                      className="rail-title"
                      variant="body2"
                      sx={{ color: 'text.primary', transition: 'color 0.15s ease' }}
                      noWrap
                    >
                      {ev.title}
                    </Typography>
                  </TruncatedTooltip>
                </Box>
                <Typography
                  variant="caption"
                  sx={{ color: 'text.secondary', fontFamily: 'monospace' }}
                >
                  {formatVolume(dailyVolume(ev))}
                </Typography>
                <Box
                  sx={{
                    width: 42,
                    height: 4,
                    borderRadius: 999,
                    bgcolor: 'action.hover',
                    overflow: 'hidden',
                  }}
                >
                  <Box sx={{ height: '100%', bgcolor: 'primary.main', width: `${heat}%` }} />
                </Box>
              </Box>
            ))}
          </Box>
        ) : (
          <Typography
            variant="body2"
            sx={{ color: 'text.secondary', textAlign: 'center', py: 1.5 }}
          >
            {t('common.noData')}
          </Typography>
        )}
      </Box>
    </Box>
  );
};
